/**
 * @fileoverview AR-924 — Matches a staged file to a student by file name ("andrew_moore.jpg",
 * "autumn-davis-p1.jpg"): accents stripped, case-insensitive, optional suffix after the last name.
 */

export interface MatchableStudent {
  readonly id: string
  readonly firstName: string
  readonly lastName: string
}

function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

export function fileBaseName(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot > 0 ? fileName.slice(0, dot) : fileName
}

/** Longest "first last" prefix wins (so "ana" never steals "ana maria"); null when none or ambiguous. */
export function matchStudentByFileName(fileName: string, students: readonly MatchableStudent[]): string | null {
  const key = normalize(fileBaseName(fileName))
  if (!key) return null

  const candidates = students
    .map(student => ({ id: student.id, nameKey: normalize(`${student.firstName} ${student.lastName}`) }))
    .filter(candidate => candidate.nameKey && key.startsWith(candidate.nameKey))
  if (candidates.length === 0) return null

  const longest = Math.max(...candidates.map(candidate => candidate.nameKey.length))
  const winners = candidates.filter(candidate => candidate.nameKey.length === longest)
  return winners.length === 1 ? winners[0].id : null
}
